import {publishOptimisticFields} from '@github-ui/optimistic-connection/connectionMutationBridge'
import {issuePath} from '@github-ui/paths'
import {useMutation} from '@github-ui/react-query'
import {updateIssueFieldValueMutation as updateIssueFieldValueQueryId} from '@github-ui/server-persisted-queries'
import type {UpdateIssueFieldValueMutationMutation} from '@github-ui/server-persisted-queries/types/updateIssueFieldValueMutation'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useCallback} from 'react'
import {useRelayEnvironment} from 'react-relay'
import type {Environment} from 'relay-runtime'

import {executeNativeCreateIssueFieldValueMutation} from './create-issue-field-value-mutation'
import {
  type IssueFieldValueMutationInput,
  type IssueFieldValueMutationRoute,
  normalizeIssueFieldValue,
  type OptimisticIssueFieldValue,
  upsertIssueFieldValue,
} from './helpers'
import {applyOptimisticIssueFieldValueToRelay, commitIssueFieldValueToRelay} from './issue-field-value-relay-bridge'

const UPDATE_ERROR = 'Update issue field value failed'
type IssueFieldValueMutationInputWithScope = IssueFieldValueMutationInput & {fieldValueExists: boolean}
type IssueFieldValueMutationOptions = {
  input: IssueFieldValueMutationInputWithScope
  optimisticFieldValue?: OptimisticIssueFieldValue
  onCompleted?: () => void
  onError?: (error?: Error) => void
}
type IssueFieldValueMutationRequestOptions = Omit<IssueFieldValueMutationOptions, 'input'> & {
  input: IssueFieldValueMutationInput
}
type UpdateResponse = {data?: UpdateIssueFieldValueMutationMutation | null; errors?: readonly unknown[] | null}

async function executeNativeUpdateIssueFieldValueMutation(
  route: IssueFieldValueMutationRoute,
  environment: Environment,
  options: IssueFieldValueMutationRequestOptions,
) {
  const {input, optimisticFieldValue, onCompleted} = options
  const optimisticRelayUpdate =
    optimisticFieldValue && applyOptimisticIssueFieldValueToRelay(environment, input, optimisticFieldValue)
  try {
    const query = new URLSearchParams({queryId: updateIssueFieldValueQueryId})
    const path = `${issuePath({owner: route.owner, repo: route.repository, issueNumber: route.number})}/field_value.json`
    const response = await reactFetchJSON(`${path}?${query.toString()}`, {
      method: 'PATCH',
      body: {issueField: input.issueField},
    })
    if (!response.ok) throw new Error(`${UPDATE_ERROR} (${response.status})`)

    let json: UpdateResponse
    try {
      json = (await response.json()) as UpdateResponse
    } catch {
      throw new Error(`${UPDATE_ERROR}: response was invalid`)
    }
    if (json.errors?.length) {
      const message = json.errors
        .flatMap(error =>
          error && typeof error === 'object' && 'message' in error && typeof error.message === 'string'
            ? [error.message]
            : [],
        )
        .join(', ')
      throw new Error(message || `${UPDATE_ERROR}: GraphQL errors were returned`)
    }
    if (!json.data) throw new Error(`${UPDATE_ERROR}: response data was missing`)

    const payload = json.data.updateIssueFieldValue
    if (!payload) throw new Error(`${UPDATE_ERROR}: payload was missing`)
    if (!payload.issue) throw new Error(`${UPDATE_ERROR}: issue was missing`)
    if (payload.issue.id !== input.issueId) throw new Error(`${UPDATE_ERROR}: issue did not match`)
    if (!payload.issueFieldValue) throw new Error(`${UPDATE_ERROR}: issue field value was missing`)

    const confirmed = optimisticFieldValue
      ? normalizeIssueFieldValue(payload.issueFieldValue, optimisticFieldValue)
      : undefined
    if (
      optimisticFieldValue &&
      (!confirmed || payload.issueFieldValue.__typename !== optimisticFieldValue.__typename)
    ) {
      throw new Error(`${UPDATE_ERROR}: issue field value was invalid`)
    }
    commitIssueFieldValueToRelay(environment, input.issueId, input.issueField.fieldId, payload.issueFieldValue)
    if (confirmed) publishOptimisticFields().completed(upsertIssueFieldValue(input.issueId, confirmed))
    onCompleted?.()
  } finally {
    optimisticRelayUpdate?.dispose()
  }
}

export function useIssueFieldValueMutation(route: IssueFieldValueMutationRoute) {
  const environment = useRelayEnvironment()
  const {mutate} = useMutation({
    mutationKey: ['issue-metadata', 'update-issue-field-value', route],
    mutationFn: async (options: IssueFieldValueMutationOptions) => {
      const {issueId, issueField, fieldValueExists} = options.input
      const input = {issueId, issueField}
      const requestOptions = {...options, input}
      if (fieldValueExists) {
        await executeNativeUpdateIssueFieldValueMutation(route, environment, requestOptions)
      } else {
        await executeNativeCreateIssueFieldValueMutation(route, environment, requestOptions)
      }
    },
    onError: (error, options) => {
      options.onError?.(error instanceof Error ? error : new Error(UPDATE_ERROR))
    },
  })

  return useCallback((options: IssueFieldValueMutationOptions) => mutate(options), [mutate])
}
